<template>
  <div v-if="visible" class="assistente-ia">
    <!-- Se monta la primera vez que se abre y luego solo se oculta,
         así la conversación se mantiene al cambiar de página -->
    <Transition name="assistente-ia">
      <div
        v-if="montato"
        v-show="aperto"
        class="assistente-ia-panel"
        role="dialog"
        aria-label="Assistente IA"
      >
        <psi-ai-assistant
          :apiUrl.prop="aiApiUrl"
          :getToken.prop="getToken"
          :context.prop="aiContext"
          :user.prop="aiUser"
          lang="it"
          @session-expired="logout"
        >
          <button
            slot="header-actions"
            type="button"
            class="assistente-ia-chiudi"
            title="Chiudi"
            aria-label="Chiudi assistente"
            @click="chiudi"
          >
            <i class="pi pi-times"></i>
          </button>
        </psi-ai-assistant>
      </div>
    </Transition>

    <button
      type="button"
      class="assistente-ia-fab"
      :class="{ 'assistente-ia-fab--aperto': aperto }"
      :title="aperto ? 'Chiudi assistente IA' : 'Apri assistente IA'"
      :aria-label="aperto ? 'Chiudi assistente IA' : 'Apri assistente IA'"
      :aria-expanded="aperto"
      :disabled="caricamento"
      @click="toggle"
    >
      <i
        :class="
          caricamento
            ? 'pi pi-spin pi-spinner'
            : aperto
            ? 'pi pi-times'
            : 'pi pi-comments'
        "
      ></i>
    </button>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { AUTH_LOGOUT } from "@/store/actions/auth";

// Livelli che vedono l'assistente durante la fase di prova
const LIVELLI_ABILITATI = ["Amministratore"];

const store = useStore();
const route = useRoute();
const router = useRouter();

const aperto = ref(false);
const montato = ref(false);

const loggedUser = computed(() => store.getters["loggedUser"] || {});

const visible = computed(() =>
  LIVELLI_ABILITATI.includes(loggedUser.value.livelloLogin)
);

// Vuoto = modalità demo (risposte simulate) finché non esiste il gateway
const aiApiUrl = process.env.VUE_APP_AI_URL || "";

const getToken = () => store.state.auth.token;

const toId = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

// Il CRM passa gli ID nel percorso (/dettaglio-pratica/:idPratica), a volte in query
const aiContext = computed(() => ({
  route: route.name,
  idPratica: toId(route.params.idPratica ?? route.query.idPratica),
  idAnagrafica: toId(route.params.idAnagrafica ?? route.query.idAnagrafica),
}));

const aiUser = computed(() => ({
  id: toId(store.state.auth.IdUser),
  livelloLogin: loggedUser.value.livelloLogin,
  name: loggedUser.value.nome,
}));

const caricamento = ref(false);

// Il widget si scarica solo la prima volta che si apre: chi non lo usa non lo carica
async function caricaWidget() {
  if (montato.value) return true;
  caricamento.value = true;
  try {
    await import(/* webpackChunkName: "ai-widget" */ "prestitosi-ai-widget");
    montato.value = true;
    return true;
  } catch (error) {
    console.error("Impossibile caricare l'assistente IA", error);
    return false;
  } finally {
    caricamento.value = false;
  }
}

async function toggle() {
  if (aperto.value) {
    chiudi();
    return;
  }
  if (await caricaWidget()) aperto.value = true;
}

function chiudi() {
  aperto.value = false;
}

function logout() {
  store.dispatch(AUTH_LOGOUT).then(() => {
    router.push({ name: "Login" });
  });
}

function onKeydown(event) {
  if (event.key === "Escape" && aperto.value) chiudi();
}

onMounted(() => window.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
</script>

<style scoped>
/* Sotto i modali di PrimeVue (z-index da 1100) ma sopra il layout */
.assistente-ia-fab,
.assistente-ia-panel {
  position: fixed;
  z-index: 1050;
}

.assistente-ia-fab {
  right: 24px;
  bottom: 24px;
  width: 56px;
  height: 56px;
  border: 0;
  border-radius: 50%;
  background: #4f46e5;
  color: #fff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;
}

.assistente-ia-fab:hover {
  transform: scale(1.06);
  background: #4338ca;
}

.assistente-ia-fab:focus-visible {
  outline: 3px solid rgba(79, 70, 229, 0.45);
  outline-offset: 3px;
}

.assistente-ia-fab i {
  font-size: 1.4rem;
}

.assistente-ia-panel {
  right: 24px;
  bottom: 92px;
  width: 400px;
  height: min(640px, calc(100vh - 120px));
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  /* Crece desde el botón */
  transform-origin: bottom right;
}

/* Animación de apertura y cierre */
.assistente-ia-enter-active {
  transition: opacity 0.22s ease-out,
    transform 0.26s cubic-bezier(0.22, 1, 0.36, 1);
}

.assistente-ia-leave-active {
  transition: opacity 0.16s ease-in, transform 0.16s ease-in;
}

.assistente-ia-enter-from,
.assistente-ia-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.92);
}

/* El icono del botón gira al cambiar entre abrir y cerrar */
.assistente-ia-fab i {
  transition: transform 0.2s ease;
}

.assistente-ia-fab--aperto i {
  transform: rotate(90deg);
}

.assistente-ia-chiudi {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #727790;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.assistente-ia-chiudi:hover {
  background: #f4f5f8;
  color: #1a1d2e;
}

/* Su schermi piccoli la finestra occupa tutto lo schermo */
@media (max-width: 576px) {
  .assistente-ia-panel {
    inset: 0;
    width: auto;
    height: auto;
    border-radius: 0;
  }

  .assistente-ia-fab--aperto {
    display: none;
  }

  /* A pantalla completa sube desde abajo en vez de crecer desde el botón */
  .assistente-ia-enter-from,
  .assistente-ia-leave-to {
    transform: translateY(24px);
  }
}

/* Si el usuario pide menos movimiento, solo fundido */
@media (prefers-reduced-motion: reduce) {
  .assistente-ia-enter-from,
  .assistente-ia-leave-to {
    transform: none;
  }

  .assistente-ia-fab,
  .assistente-ia-fab i {
    transition: none;
  }
}
</style>
